import { Box, Stack, Typography } from "@mui/material";
import {
  FALLBACK_BANNERS,
  FIBERFIRST_BANNER_KEY,
  FIBERFIRST_ZIPLY_FIBER,
} from "../../../../Constants/constants";
import { HomepageBannersWrapper } from "../../../../Styles/GlobalStyles";
import { Theme } from "../../../../Styles/GlobalTheme";

const textColor = "#64748B";
const fallbackContent = FALLBACK_BANNERS[FIBERFIRST_BANNER_KEY];

// Sitecore sends full URLs, site-relative paths ("/-/media/...") or bare hosts ("ziplyfiber.com/...")
const resolveMediaSrc = (src?: string) => {
  const value = src?.trim();
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value) || value.startsWith("/")) return value;
  return `https://${value}`;
};

const getFirstText = (items: any) =>
  Array.isArray(items) ? items.find((item: any) => item?.text)?.text : undefined;

const isLogoItem = (item: any) => item?.type === "icon" || item?.type === "image";

export const FiberFirstBanner = ({ banner }: { banner: any }) => {
  if (String(banner?.visibility) === "false") return null;

  const content = banner?.content ?? {};
  const label = getFirstText(content.label) ?? getFirstText(fallbackContent.label);
  const subheading = getFirstText(content.subheading) ?? getFirstText(fallbackContent.subheading);
  const iconSrc = resolveMediaSrc(content.icon?.src);
  const headingItems =
    Array.isArray(content.heading) && content.heading.some((item: any) => item?.text)
      ? content.heading
      : fallbackContent.heading;
  const headingLabel = headingItems
    .map((item: any) => (isLogoItem(item) ? FIBERFIRST_ZIPLY_FIBER : item?.text))
    .filter(Boolean)
    .join(" ");

  return (
    <HomepageBannersWrapper sx={{ mt: 0 }}>
      <Stack
        data-testid="fiberfirst-banner"
        spacing={1}
        sx={{
          backgroundColor: `${Theme.palette.primary.main}0D`,
          borderLeft: `4px solid ${Theme.palette.primary.main}`,
          borderRadius: "8px",
          px: { xs: 2, sm: 3 },
          pt: 2,
          pb: 2,
        }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            alignSelf: "flex-start",
            backgroundColor: Theme.palette.primary.main,
            color: "#FFFFFF",
            borderRadius: "24px",
            px: 1.25,
            py: "5px",
          }}>
          {iconSrc && (
            <Box component="img" src={iconSrc} alt="" sx={{ width: "16px", height: "16px" }} />
          )}
          <Typography sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase" }}>
            {label}
          </Typography>
        </Stack>
        <Typography
          component="h2"
          aria-label={headingLabel}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 1,
            color: textColor,
            fontWeight: 600,
            fontSize: { xs: "18px", sm: "20px" },
            lineHeight: 1.3,
          }}>
          {headingItems.map((item: any, index: number) => {
            if (!isLogoItem(item)) {
              return item?.text ? <span key={index}>{item.text}</span> : null;
            }
            const logoSrc = resolveMediaSrc(item.src);
            // No logo from Sitecore: keep the sentence readable with the brand name as text
            return logoSrc ? (
              <Box
                key={index}
                component="img"
                src={logoSrc}
                alt={FIBERFIRST_ZIPLY_FIBER}
                sx={{
                  height: { xs: "44px", sm: "52px" },
                  my: { xs: "-10px", sm: "-13px" },
                  // The SVG has empty padding on both sides; trim it so the logo sits like a word
                  ml: { xs: "-8px", sm: "-10px" },
                  mr: { xs: "-11px", sm: "-13px" },
                }}
              />
            ) : (
              <span key={index}>{FIBERFIRST_ZIPLY_FIBER}</span>
            );
          })}
        </Typography>
        <Typography sx={{ color: textColor, fontSize: "14px" }}>{subheading}</Typography>
      </Stack>
    </HomepageBannersWrapper>
  );
};
